import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useV2ListUsersForAccountQuery } from "@generated";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { BusinessApprovalForm } from "../BusinessApprovalForm/BusinessApprovalForm";
import { useApprovalLevelMutations } from "../api/useApprovalLevelMutations";
import type { ApprovalLevelFormValues } from "../types/ApprovalLevel";

const FORM_ID = "business-approval-form";

const initial: ApprovalLevelFormValues = {
  name: "",
  signaturesRequired: 1,
  submitterMayApprove: false,
  limitApprovalsToTeam: false,
  triggers: [],
  approvers: [],
};

export const New = () => {
  const navigate = useNavigate();
  const { create } = useApprovalLevelMutations();
  const { data: usersData } = useV2ListUsersForAccountQuery({});
  const [submitting, setSubmitting] = useState(false);

  const availableUsers = useMemo(
    () =>
      (usersData?.data ?? []).map((u: any) => ({
        id: String(u.id),
        name: u.attributes?.name ?? u.attributes?.identifier ?? "",
      })),
    [usersData]
  );
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const handleSubmit = async (values: ApprovalLevelFormValues) => {
    setSubmitting(true);
    try {
      const result: any = await create("business", values);
      const newId = result?.data?.id;
      if (newId) {
        navigate(`/admin/approval_levels/${newId}/edit`, {
          state: {
            toast: { variant: "success", text: "Business approval created." },
          },
        });
      } else {
        setToast({ variant: "success", text: "Business approval created." });
      }
    } catch (err) {
      console.error("Failed to create business approval", err);
      setToast({
        variant: "danger",
        text: "Could not create business approval.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Add a Business Approval"
        formId={FORM_ID}
        onCancel={() => navigate("/admin/approval_levels")}
        isSubmitting={submitting}
      >
        <BusinessApprovalForm
          id={FORM_ID}
          initialValues={initial}
          availableUsers={availableUsers}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
